/*!
 * Uberdrinks theme behaviours — dependency-free vanilla JS.
 * Implements every hook documented in shopify-theme/CONVENTIONS.md's
 * "JS contract" table. No imports, no bundler.
 */
(function () {
  "use strict";

  var T = window.Uberdrinks || {};
  var routes = T.routes || {};
  var strings = T.strings || {};

  var reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  window.matchMedia("(prefers-reduced-motion: reduce)").addEventListener("change", function (e) {
    reducedMotion = e.matches;
  });

  /* ============================================================
   * Utilities
   * ========================================================== */
  function qsa(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }
  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }
  function on(root, event, selector, handler) {
    root.addEventListener(event, function (e) {
      var target = e.target.closest(selector);
      if (target && root.contains(target)) handler(e, target);
    });
  }
  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments,
        ctx = this;
      window.clearUberdrinksut(t);
      t = window.setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }
  function parseHTML(html) {
    var tpl = document.createElement("template");
    tpl.innerHTML = html.trim();
    return tpl.content;
  }

  /* ============================================================
   * data-reveal — scroll reveal (once)
   * ========================================================== */
  function initReveal() {
    var els = qsa("[data-reveal]:not(.is-revealed)");
    if (!els.length) return;
    if (!("IntersectionObserver" in window) || reducedMotion) {
      els.forEach(function (el) {
        el.classList.add("is-revealed");
      });
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
    );
    els.forEach(function (el) {
      io.observe(el);
    });
  }

  /* ============================================================
   * data-smart-image / data-img-placeholder — blur-up
   * ========================================================== */
  function enhanceImage(img) {
    if (img.dataset.smartImageBound) return;
    img.dataset.smartImageBound = "1";
    var wrapper = img.closest("span, div");
    var placeholder = wrapper ? wrapper.querySelector("[data-img-placeholder]") : null;
    function reveal() {
      img.classList.add("img-shown");
      img.dataset.loaded = "true";
      if (placeholder) placeholder.classList.add("img-placeholder-out");
    }
    if (img.complete && img.naturalWidth > 0) {
      reveal();
    } else {
      img.addEventListener("load", reveal, { once: true });
      img.addEventListener("error", reveal, { once: true });
    }
  }
  function initSmartImages(root) {
    qsa("[data-smart-image]", root || document).forEach(enhanceImage);
  }

  /* ============================================================
   * data-count-up
   * ========================================================== */
  function initCountUp() {
    var els = qsa("[data-count-up]:not([data-count-bound])");
    if (!els.length || !("IntersectionObserver" in window)) {
      els.forEach(runCountUp);
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            runCountUp(entry.target);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.25, rootMargin: "0px 0px -12% 0px" },
    );
    els.forEach(function (el) {
      io.observe(el);
    });
  }
  function runCountUp(el) {
    el.dataset.countBound = "1";
    var target = parseFloat(el.getAttribute("data-count-up")) || 0;
    var decimals = parseInt(el.getAttribute("data-count-decimals") || "0", 10);
    var suffix = el.getAttribute("data-count-suffix") || "";
    if (reducedMotion) {
      el.textContent = target.toFixed(decimals) + suffix;
      return;
    }
    var duration = 1400;
    var start = null;
    function tick(now) {
      if (start === null) start = now;
      var p = Math.min((now - start) / duration, 1);
      var eased = 1 - Math.pow(1 - p, 3);
      el.textContent = (target * eased).toFixed(decimals) + suffix;
      if (p < 1) window.requestAnimationFrame(tick);
    }
    window.requestAnimationFrame(tick);
  }

  /* ============================================================
   * data-tabs / data-tab / data-panel
   * ========================================================== */
  function initTabs() {
    on(document, "click", "[data-tab]", function (e, btn) {
      var wrapper = btn.closest("[data-tabs]");
      if (!wrapper) return;
      var index = btn.getAttribute("data-tab");
      setTab(wrapper, index);
    });
  }
  function setTab(wrapper, index) {
    var buttons = qsa("[data-tab]", wrapper);
    var panels = qsa("[data-panel]", wrapper);

    buttons.forEach(function (b) {
      var active = b.getAttribute("data-tab") === String(index);
      b.setAttribute("aria-selected", String(active));
      b.classList.toggle("text-primary", active);
      b.classList.toggle("text-foreground", active && b.getAttribute("role") === "tab");
      b.classList.toggle("text-muted-foreground", !active);
      b.classList.toggle("hover:text-foreground", !active);

      var bar = b.querySelector("span");
      if (bar) {
        bar.classList.toggle("scale-x-100", active);
        bar.classList.toggle("scale-x-0", !active);
      }
    });

    panels.forEach(function (p) {
      var match = p.getAttribute("data-panel") === String(index);
      p.hidden = !match;
      p.setAttribute("aria-hidden", String(!match));
      p.classList.toggle("hidden", !match);
    });

    wrapper.setAttribute("data-active-tab", String(index));
    wrapper.dispatchEvent(new CustomEvent("uberdrinks:tab", { detail: { index: String(index) }, bubbles: true }));

    // Recalculate carousel controls after a panel becomes visible.
    panels.forEach(function (p) {
      if (!p.hidden) {
        qsa("[data-carousel]", p).forEach(function (carousel) {
          var scroller = qs("[data-scroller]", carousel);
          if (scroller) scroller.dispatchEvent(new Event("scroll"));
        });
      }
    });
  }

  /* ============================================================
   * data-scroller / data-scroll-prev / data-scroll-next / data-scroll-progress
   * ========================================================== */
  function initScrollers() {
    qsa("[data-carousel]").forEach(function (root) {
      var scroller = qs("[data-scroller]", root);
      if (!scroller || scroller.dataset.scrollerBound) return;
      scroller.dataset.scrollerBound = "1";
      var prevBtn = qs("[data-scroll-prev]", root);
      var nextBtn = qs("[data-scroll-next]", root);
      var progressEl = qs("[data-scroll-progress]", root);

      function update() {
        var max = scroller.scrollWidth - scroller.clientWidth;
        var progress = max > 0 ? scroller.scrollLeft / max : 0;
        var atStart = scroller.scrollLeft <= 4;
        var atEnd = max <= 0 || scroller.scrollLeft >= max - 4;
        if (progressEl) progressEl.style.transform = "scaleX(" + progress + ")";
        if (prevBtn) prevBtn.disabled = atStart;
        if (nextBtn) nextBtn.disabled = atEnd;
      }
      scroller.addEventListener("scroll", update, { passive: true });
      window.addEventListener("resize", update);
      update();

      function scrollByDir(dir) {
        scroller.scrollBy({
          left: dir * Math.max(scroller.clientWidth * 0.62, 260),
          behavior: reducedMotion ? "auto" : "smooth",
        });
      }
      if (prevBtn) prevBtn.addEventListener("click", function () { scrollByDir(-1); });
      if (nextBtn) nextBtn.addEventListener("click", function () { scrollByDir(1); });
    });
  }

  /* ============================================================
   * data-autoplay / data-slides / data-slide / data-dot
   * ========================================================== */
  function initAutoplay() {
    qsa("[data-slides]").forEach(function (root) {
      if (root.dataset.autoplayBound) return;
      root.dataset.autoplayBound = "1";
      var delay = parseInt(root.getAttribute("data-autoplay") || "0", 10);
      var slides = qsa("[data-slide]", root);
      var dots = qsa("[data-dot]", root);
      var contents = qsa("[data-slide-content]", root);
      var length = slides.length;
      var index = 0;
      var timer = null;
      var paused = false;

      function apply() {
        slides.forEach(function (s) {
          var i = s.getAttribute("data-slide");
          var active = Number(i) === index;
          s.setAttribute("aria-hidden", String(!active));
          s.classList.toggle("opacity-100", active);
          s.classList.toggle("pointer-events-none", !active);
          s.classList.toggle("opacity-0", !active);
        });
        contents.forEach(function (c) {
          var i = c.getAttribute("data-slide-content");
          var active = Number(i) === index;
          c.setAttribute("aria-hidden", String(!active));
          c.classList.toggle("opacity-100", active);
          c.classList.toggle("opacity-0", !active);
          c.classList.toggle("pointer-events-none", !active);
        });
        dots.forEach(function (d) {
          var i = d.getAttribute("data-dot");
          var active = Number(i) === index;
          d.setAttribute("aria-selected", String(active));
          d.classList.toggle("bg-primary-soft", active);
          d.classList.toggle("text-foreground", active);
          d.classList.toggle("text-primary/60", !active);
        });
      }
      function goTo(i) {
        index = ((i % length) + length) % length;
        apply();
      }
      function next() { goTo(index + 1); }
      function prev() { goTo(index - 1); }
      function restart() {
        window.clearInterval(timer);
        if (paused || reducedMotion || length < 2 || !delay) return;
        timer = window.setInterval(next, delay);
      }
      root.addEventListener("mouseenter", function () { paused = true; restart(); });
      root.addEventListener("mouseleave", function () { paused = false; restart(); });
      root.addEventListener("focusin", function () { paused = true; restart(); });
      root.addEventListener("focusout", function () { paused = false; restart(); });
      dots.forEach(function (d) {
        d.addEventListener("click", function () {
          goTo(Number(d.getAttribute("data-dot")));
          restart();
        });
      });
      qsa("[data-slide-prev]", root).forEach(function (b) {
        b.addEventListener("click", function () { prev(); restart(); });
      });
      qsa("[data-slide-next]", root).forEach(function (b) {
        b.addEventListener("click", function () { next(); restart(); });
      });
      apply();
      restart();
    });
  }

  /* ============================================================
   * Overlays — data-open / data-close / data-overlay / focus trap / scroll lock
   * ========================================================== */
  var openOverlays = 0;
  var lastFocused = null;

  function getOverlay(name) {
    return qs('[data-overlay="' + name + '"]');
  }
  function lockBody() {
    openOverlays++;
    document.body.style.overflow = "hidden";
  }
  function unlockBody() {
    openOverlays = Math.max(0, openOverlays - 1);
    if (openOverlays === 0) document.body.style.overflow = "";
  }
  function focusablesIn(root) {
    return qsa(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      root,
    ).filter(function (el) {
      return el.offsetParent !== null;
    });
  }
  function openOverlay(name) {
    var root = getOverlay(name);
    if (!root || root.classList.contains("is-open")) return;
    lastFocused = document.activeElement;
    root.classList.add("is-open");
    root.setAttribute("aria-hidden", "false");
    root.classList.remove("pointer-events-none");
    root.classList.add("pointer-events-auto");
    var scrim = qs("[data-overlay-scrim]", root);
    var panel = qs("[data-overlay-panel]", root);
    if (scrim) {
      scrim.classList.remove("opacity-0");
      scrim.classList.add("opacity-100");
    }
    if (panel) {
      var centered = panel.classList.contains("-translate-x-1/2") || panel.classList.contains("-translate-y-1/2");
      panel.classList.remove("opacity-0", "scale-[0.97]", "blur-[4px]");
      if (panel.classList.contains("translate-x-[110%]")) {
        panel.classList.remove("translate-x-[110%]");
        panel.classList.add("translate-x-0");
      }
      if (panel.classList.contains("-translate-y-4") || panel.classList.contains("-translate-y-2")) {
        panel.classList.remove("-translate-y-4", "-translate-y-2");
        panel.classList.add("translate-y-0");
      }
      panel.classList.add("opacity-100", "scale-100", "blur-0");
      if (centered) {
        panel.classList.remove("translate-x-0", "translate-y-0");
      }
      window.setTimeout(function () {
        var focusables = focusablesIn(panel);
        (focusables[0] || panel).focus();
      }, reducedMotion ? 0 : 60);
    }
    lockBody();
    initSmartImages(root);
    root.dispatchEvent(new CustomEvent("uberdrinks:overlay-open", { detail: { name: name }, bubbles: true }));
  }
  function closeOverlay(name) {
    var root = getOverlay(name);
    if (!root || !root.classList.contains("is-open")) return;
    root.classList.remove("is-open");
    root.setAttribute("aria-hidden", "true");
    root.classList.add("pointer-events-none");
    root.classList.remove("pointer-events-auto");
    var scrim = qs("[data-overlay-scrim]", root);
    var panel = qs("[data-overlay-panel]", root);
    if (scrim) {
      scrim.classList.add("opacity-0");
      scrim.classList.remove("opacity-100");
    }
    if (panel) {
      panel.classList.remove("opacity-100", "scale-100", "blur-0");
      if (panel.classList.contains("right-0")) {
        panel.classList.remove("translate-x-0");
        panel.classList.add("translate-x-[110%]", "opacity-0");
      }
      else if (panel.classList.contains("inset-x-0")) {
        panel.classList.remove("translate-y-0");
        panel.classList.add("-translate-y-4", "opacity-0");
      }
      else panel.classList.add("scale-[0.97]", "opacity-0", "blur-[4px]");
    }
    unlockBody();
    if (lastFocused && typeof lastFocused.focus === "function") lastFocused.focus();
    root.dispatchEvent(new CustomEvent("uberdrinks:overlay-close", { detail: { name: name }, bubbles: true }));
  }

  function initOverlays() {
    on(document, "click", "[data-open]", function (e, el) {
      e.preventDefault();
      openOverlay(el.getAttribute("data-open"));
    });
    on(document, "click", "[data-close]", function (e, el) {
      e.preventDefault();
      closeOverlay(el.getAttribute("data-close"));
    });
    document.addEventListener("keydown", function (e) {
      if (e.key !== "Escape") return;
      qsa('[data-overlay].is-open').forEach(function (root) {
        closeOverlay(root.getAttribute("data-overlay"));
      });
    });
    // Focus trap for whichever overlay is open.
    document.addEventListener("keydown", function (e) {
      if (e.key !== "Tab") return;
      var open = qs('[data-overlay].is-open [data-overlay-panel]');
      if (!open) return;
      var focusables = focusablesIn(open);
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  /* ============================================================
   * Toasts (branded sonner-style)
   * ========================================================== */
  function showToast(message, opts) {
    opts = opts || {};
    var viewport = qs("#ToastViewport");
    if (!viewport) return;
    var toast = document.createElement("div");
    toast.className =
      "toast-uberdrinks pointer-events-auto flex w-[min(92vw,23rem)] items-start gap-3 rounded-2xl border border-hairline bg-card px-4 py-3.5 shadow-float";
    var html =
      '<span class="mt-0.5 grid size-8 shrink-0 place-items-center rounded-full bg-primary-soft text-primary">' +
      '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" class="size-4"><path d="M20 6 9 17l-5-5"/></svg>' +
      "</span>" +
      '<span class="min-w-0 flex-1">' +
      '<span class="block text-[14px] font-medium text-foreground">' + message + "</span>" +
      (opts.description ? '<span class="mt-0.5 block text-[13px] text-muted-foreground">' + opts.description + "</span>" : "") +
      (opts.actionLabel
        ? '<button type="button" data-toast-action class="mt-2 inline-flex items-center text-[12px] font-semibold uppercase tracking-[0.14em] text-primary t-hover hover:opacity-70">' + opts.actionLabel + "</button>"
        : "") +
      "</span>" +
      '<button type="button" data-toast-dismiss aria-label="' + (strings.close || "Close") + '" class="grid size-7 shrink-0 place-items-center rounded-full text-muted-foreground t-hover hover:text-foreground">' +
      '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-3.5"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>' +
      "</button>";
    toast.innerHTML = html;
    viewport.appendChild(toast);

    function remove() {
      if (!toast.parentNode) return;
      toast.style.transition = "opacity 240ms ease, transform 240ms ease";
      toast.style.opacity = "0";
      toast.style.transform = "translateY(6px) scale(0.98)";
      window.setTimeout(function () {
        toast.remove();
      }, 240);
    }
    var actionBtn = qs("[data-toast-action]", toast);
    if (actionBtn && typeof opts.onAction === "function") {
      actionBtn.addEventListener("click", function () {
        opts.onAction();
        remove();
      });
    }
    qs("[data-toast-dismiss]", toast).addEventListener("click", remove);
    window.setTimeout(remove, opts.duration || 4200);
  }

  /* ============================================================
   * AJAX cart — add / count / drawer refresh
   * ========================================================== */
  function updateCartCountBadges(count) {
    qsa("[data-cart-count-badge]").forEach(function (el) {
      el.textContent = count;
      el.classList.remove("cart-pop");
      void el.offsetWidth;
      el.classList.add("cart-pop");
    });
  }

  function toggleCartSkeleton(show) {
    var sk = qs("[data-cart-skeleton]");
    var list = qs("[data-cart-lines]");
    if (sk) sk.classList.toggle("hidden", !show), sk.classList.toggle("flex", !!show);
    if (list) list.classList.toggle("hidden", !!show);
  }

  function refreshCartDrawer() {
    toggleCartSkeleton(true);
    return fetch(routes.cart_url ? "/?section_id=cart-drawer" : "/?section_id=cart-drawer", {
      headers: { "X-Requested-With": "XMLHttpRequest" },
    })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = parseHTML(html);
        var fresh = doc.querySelector("#CartDrawerInner");
        var current = qs("#CartDrawerInner");
        if (fresh && current) {
          current.innerHTML = fresh.innerHTML;
          initSmartImages(current);
        }
        return fetch("/cart.js").then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        toggleCartSkeleton(false);
        if (cart) updateCartCountBadges(cart.item_count);
        return cart;
      })
      .catch(function (err) {
        toggleCartSkeleton(false);
        throw err;
      });
  }

  function addToCart(variantId, qty) {
    return fetch(routes.cart_add_url || "/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: variantId, quantity: qty || 1 }),
    }).then(function (r) {
      if (!r.ok) throw new Error("add-failed");
      return r.json();
    });
  }

  function initQuickAdd() {
    on(document, "click", "[data-quick-add]", function (e, btn) {
      if (btn.disabled) return;
      e.preventDefault();
      var variantId = btn.getAttribute("data-quick-add");
      btn.disabled = true;
      btn.classList.add("opacity-60");
      addToCart(variantId, 1)
        .then(function (item) {
          return refreshCartDrawer().then(function () {
            showToast((item.product_title || item.title || "Item") + " added to bag", {
              description: item.price ? "€" + (item.price / 100).toFixed(2) + " · free shipping over €80" : "",
              actionLabel: strings.viewBag || "View bag",
              onAction: function () { openOverlay("cart"); },
              duration: 4200,
            });
          });
        })
        .catch(function () {
          showToast(strings.error || "Something went wrong. Please try again.", { duration: 4200 });
        })
        .finally(function () {
          btn.disabled = false;
          btn.classList.remove("opacity-60");
        });
    });
  }

  /* ============================================================
   * Optimistic cart qty / remove with spinner + rollback
   * ========================================================== */
  function setLineBusy(key, busy) {
    var line = qs('[data-cart-line="' + key + '"]');
    if (!line) return;
    line.classList.toggle("is-busy", !!busy);
    if (busy) line.setAttribute("aria-busy", "true");
    else line.removeAttribute("aria-busy");
    qsa("[data-qty-spinner]", line).forEach(function (el) { el.classList.toggle("hidden", !busy); });
    qsa("[data-qty-value]", line).forEach(function (el) { el.classList.toggle("hidden", busy); });
    qsa("[data-cart-qty]", line).forEach(function (el) { el.disabled = busy; });
    var removeBtn = qs("[data-cart-remove='" + key + "']", line);
    if (removeBtn) {
      removeBtn.disabled = busy;
      var icon = qs("[data-remove-icon]", removeBtn);
      var spinner = qs("[data-remove-spinner]", removeBtn);
      if (icon) icon.classList.toggle("hidden", busy);
      if (spinner) spinner.classList.toggle("hidden", !busy);
    }
  }
  function showCartError(message, retry) {
    var banner = qs("#CartErrorBanner");
    if (!banner) return;
    var msgEl = qs("[data-cart-error-message]", banner);
    if (msgEl) msgEl.textContent = message || strings.error || "Something went wrong.";
    banner.classList.remove("hidden");
    banner.classList.add("flex");
    var retryBtn = qs("[data-cart-retry]", banner);
    if (retryBtn) retryBtn.onclick = retry || null;
  }
  function hideCartError() {
    var banner = qs("#CartErrorBanner");
    if (!banner) return;
    banner.classList.add("hidden");
    banner.classList.remove("flex");
  }

  function cartChange(key, quantity) {
    return fetch(routes.cart_change_url || "/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: key, quantity: quantity }),
    }).then(function (r) {
      if (!r.ok) throw new Error("change-failed");
      return r.json();
    });
  }

  function mutateLine(key, nextQty, failMessage) {
    var kind = nextQty <= 0 ? "remove" : "qty";
    if (kind === "remove") {
      var line = qs('[data-cart-line="' + key + '"]');
      if (line) line.classList.replace("cart-line", "cart-line-leaving");
    }
    setLineBusy(key, true);
    hideCartError();
    window.setTimeout(
      function () {
        cartChange(key, nextQty)
          .then(function () { return refreshCartDrawer(); })
          .catch(function () {
            setLineBusy(key, false);
            showCartError(failMessage, function () { mutateLine(key, nextQty, failMessage); });
            refreshCartDrawer();
          });
      },
      kind === "remove" ? 320 : 0,
    );
  }

  function initCartLineActions() {
    on(document, "click", "[data-cart-qty]", function (e, btn) {
      var key = btn.getAttribute("data-cart-qty");
      var delta = parseInt(btn.getAttribute("data-qty-delta"), 10) || 0;
      var valueEl = qs('[data-cart-line="' + key + '"] [data-qty-value]');
      var current = valueEl ? parseInt(valueEl.textContent, 10) || 0 : 0;
      mutateLine(key, current + delta, "Quantity update failed. Your bag was restored.");
    });
    on(document, "click", "[data-cart-remove]", function (e, btn) {
      var key = btn.getAttribute("data-cart-remove");
      mutateLine(key, 0, "We couldn't remove that item. Please try again.");
    });
    on(document, "click", "[data-cart-error-dismiss]", function () {
      hideCartError();
    });
  }

  /* ============================================================
   * Quick view
   * ========================================================== */
  function initQuickView() {
    on(document, "click", "[data-quick-view]", function (e, el) {
      e.preventDefault();
      var url = el.getAttribute("data-product-url") || (el.closest("[data-product-url]") || {}).getAttribute && el.closest("[data-product-url]").getAttribute("data-product-url");
      if (!url) {
        var handle = el.getAttribute("data-quick-view");
        url = "/products/" + handle;
      }
      var container = qs("#QuickViewInner");
      if (container) container.innerHTML = '<div class="p-10 text-center text-muted-foreground">Loading…</div>';
      openOverlay("quick-view");
      fetch(url + "?view=quick")
        .then(function (r) { return r.text(); })
        .then(function (html) {
          if (container) {
            container.innerHTML = html;
            initSmartImages(container);
          }
        })
        .catch(function () {
          if (container) container.innerHTML = '<div class="p-10 text-center text-muted-foreground">' + (strings.error || "Something went wrong.") + "</div>";
        });
    });
  }

  /* ============================================================
   * Video lightbox
   * ========================================================== */
  function ensureVideoOverlay() {
    var existing = getOverlay("video");
    if (existing) return existing;
    var wrap = document.createElement("div");
    wrap.innerHTML =
      '<div data-overlay="video" aria-hidden="true" class="fixed inset-0 z-[90] pointer-events-none">' +
      '<button type="button" tabindex="-1" aria-label="Close overlay" data-overlay-scrim data-close="video" class="absolute inset-0 h-full w-full cursor-default bg-primary-deep/40 backdrop-blur-[3px] transition-opacity duration-[var(--duration-drawer)] ease-[var(--ease-drawer)] opacity-0"></button>' +
      '<div role="dialog" aria-modal="true" aria-label="Video" data-overlay-panel class="absolute will-change-transform left-1/2 top-1/2 w-[min(92vw,64rem)] -translate-x-1/2 -translate-y-1/2 px-0 [transition:transform_620ms_var(--ease-luxe),opacity_420ms_var(--ease-luxe),filter_420ms_var(--ease-luxe)] scale-[0.97] opacity-0 blur-[4px]">' +
      '<div class="overflow-hidden rounded-[2rem] bg-primary-deep shadow-float">' +
      '<div class="media aspect-video rounded-none" id="VideoLightboxMedia"></div>' +
      '<div class="flex items-center justify-between gap-4 px-6 py-5">' +
      '<div class="font-display text-lg text-primary-foreground" id="VideoLightboxTitle">Uberdrinks brand film</div>' +
      '<button type="button" data-close="video" aria-label="' + (strings.close || "Close") + '" class="grid size-10 place-items-center rounded-full border border-hairline bg-card text-foreground/70 t-hover hover:rotate-90 hover:bg-primary hover:text-primary-foreground">' +
      '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-4"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>' +
      "</button></div></div></div></div>";
    document.body.appendChild(wrap.firstElementChild);
    return getOverlay("video");
  }
  function initVideoLightbox() {
    on(document, "click", "[data-video-open]", function (e, el) {
      e.preventDefault();
      ensureVideoOverlay();
      var url = el.getAttribute("data-video-open");
      var media = qs("#VideoLightboxMedia");
      var titleEl = qs("#VideoLightboxTitle");
      if (titleEl) titleEl.textContent = el.getAttribute("data-video-title") || "Uberdrinks brand film";
      if (media) {
        if (/youtube|vimeo|\.mp4|\.webm/.test(url)) {
          if (/\.mp4|\.webm/.test(url)) {
            media.innerHTML = '<video src="' + url + '" class="size-full object-cover" controls autoplay playsinline></video>';
          } else {
            media.innerHTML = '<iframe src="' + url + '" class="size-full" allow="autoplay; fullscreen" allowfullscreen frameborder="0"></iframe>';
          }
        } else {
          media.innerHTML = '<div class="absolute inset-0 grid place-items-center text-primary-foreground/80 text-[14px]">Video preview unavailable.</div>';
        }
      }
      openOverlay("video");
    });
  }

  /* ============================================================
   * Before / after drag comparison
   * ========================================================== */
  function initBeforeAfter() {
    qsa("[data-before-after]").forEach(function (root) {
      if (root.dataset.baBound) return;
      root.dataset.baBound = "1";
      var range = qs("[data-ba-range]", root);
      var clip = qs("[data-ba-clip]", root);
      var cliphandle = qs("[data-ba-divider]", root);
      var clippointer = qs("[data-ba-handle]", root);
      var cliptextbefore = qs("[data-ba-label-before]", root);
      var cliptextafter = qs("[data-ba-label-after]", root);
      if (!range || !clip) return;

      function apply(value) {
        clip.style.clipPath = "inset(0 " + (100 - value) + "% 0 0)";
        cliphandle.style.left = (value) + "%";
        clippointer.style.left = (value) + "%";
        range.value = value;
        range.setAttribute("aria-valuenow", String(value));
        if (value < 30) cliptextbefore.classList.add('opacity-0');
        else cliptextbefore.classList.remove('opacity-0');
        if (value > 70) cliptextafter.classList.add('opacity-0');
        else cliptextafter.classList.remove('opacity-0');
      }
      apply(parseFloat(range.value) || 50);

      function fromClientX(clientX) {
        var rect = root.getBoundingClientRect();
        var pct = ((clientX - rect.left) / rect.width) * 100;
        apply(Math.min(100, Math.max(0, pct)));
      }
      var dragging = false;
      root.addEventListener("pointerdown", function (e) {
        dragging = true;
        root.setPointerCapture(e.pointerId);
        fromClientX(e.clientX);
      });
      root.addEventListener("pointermove", function (e) {
        if (!dragging) return;
        fromClientX(e.clientX);
      });
      ["pointerup", "pointercancel"].forEach(function (evt) {
        root.addEventListener(evt, function (e) {
          dragging = false;
          if (root.hasPointerCapture && root.hasPointerCapture(e.pointerId)) {
            root.releasePointerCapture(e.pointerId);
          }
        });
      });
      range.addEventListener("input", function () {
        apply(parseFloat(range.value));
      });
      range.addEventListener("keydown", function (e) {
        var step = 5;
        var value = parseFloat(range.value) || 50;
        if (e.key === "ArrowLeft") apply(Math.max(0, value - step));
        else if (e.key === "ArrowRight") apply(Math.min(100, value + step));
        else if (e.key === "Home") apply(0);
        else if (e.key === "End") apply(100);
      });
    });
  }

  /* ============================================================
   * FAQ accordion
   * ========================================================== */
  function initAccordion() {
    qsa("[data-accordion]").forEach(function (root) {

      if (root.dataset.accordionBound) return;

      root.dataset.accordionBound = "1";

      var triggers = qsa("[data-accordion-trigger]", root);

      triggers.forEach(function (trigger) {

        trigger.addEventListener("click", function () {

          var panelId = trigger.getAttribute("aria-controls");
          var panel = panelId
            ? document.getElementById(panelId)
            : trigger.nextElementSibling;

          var expanded =
            trigger.getAttribute("aria-expanded") === "true";

          // Close all other items
          triggers.forEach(function (t) {

            if (t === trigger) return;

            t.setAttribute("aria-expanded", "false");

            var pid = t.getAttribute("aria-controls");
            var p = pid
              ? document.getElementById(pid)
              : t.nextElementSibling;

            if (p) {
              p.setAttribute("aria-hidden", "true");

              p.classList.remove(
                "grid-rows-[1fr]",
                "opacity-100"
              );

              p.classList.add(
                "grid-rows-[0fr]",
                "opacity-0"
              );
            }

            // Other item icon → PLUS
            var otherIcon = t.querySelector("[data-accordion-icon]");

            if (otherIcon) {
              var otherPlus = otherIcon.querySelector("[data-icon-plus]");
              var otherMinus = otherIcon.querySelector("[data-icon-minus]");

              if (otherPlus) otherPlus.classList.remove("hidden");
              if (otherMinus) otherMinus.classList.add("hidden");
            }

            var otherItem = t.parentElement;

            otherItem.classList.remove(
              "bg-primary-foreground/25"
            );

            otherItem.classList.add(
              "bg-primary-foreground/10",
              "hover:bg-primary-foreground/15"
            );
          });

          // Toggle clicked item
          var newExpanded = !expanded;

          trigger.setAttribute(
            "aria-expanded",
            String(newExpanded)
          );

          // Clicked item icon
          var icon = trigger.querySelector("[data-accordion-icon]");

          if (icon) {
            var plusIcon = icon.querySelector("[data-icon-plus]");
            var minusIcon = icon.querySelector("[data-icon-minus]");

            if (plusIcon) {
              plusIcon.classList.toggle("hidden", newExpanded);
            }

            if (minusIcon) {
              minusIcon.classList.toggle("hidden", !newExpanded);
            }
          }

          if (panel) {

            panel.setAttribute(
              "aria-hidden",
              String(!newExpanded)
            );

            if (newExpanded) {

              panel.classList.remove(
                "grid-rows-[0fr]",
                "opacity-0"
              );

              panel.classList.add(
                "grid-rows-[1fr]",
                "opacity-100"
              );

              var item = trigger.parentElement;

              item.classList.remove(
                "bg-primary-foreground/10",
                "hover:bg-primary-foreground/15"
              );

              item.classList.add(
                "bg-primary-foreground/25"
              );

            } else {

              panel.classList.remove(
                "grid-rows-[1fr]",
                "opacity-100"
              );

              panel.classList.add(
                "grid-rows-[0fr]",
                "opacity-0"
              );
            }
          }
        });
      });
    });
  }

  /* ============================================================
   * Chrome: back-to-top, sticky-cta, scroll progress, header scrolled
   * ========================================================== */
  function initChrome() {
    var backToTop = qs("[data-back-to-top]");
    var stickyCta = qs("[data-sticky-cta]");
    var progressBar = qs("[data-scroll-progress-bar]");
    var header = qs("[data-header]") || qs("header");

    function onScroll() {
      var y = window.scrollY;
      var vh = window.innerHeight;
      if (backToTop) {
        var show = y > vh * 0.9;
        backToTop.classList.toggle("translate-y-0", show);
        backToTop.classList.toggle("opacity-100", show);
        backToTop.classList.toggle("pointer-events-none", !show);
        backToTop.classList.toggle("translate-y-4", !show);
        backToTop.classList.toggle("opacity-0", !show);
      }
      if (stickyCta) {
        var showCta = y > vh * 0.8;
        stickyCta.classList.toggle("translate-y-0", showCta);
        stickyCta.classList.toggle("translate-y-full", !showCta);
      }
      if (progressBar) {
        var max = document.documentElement.scrollHeight - vh;
        var progress = max > 0 ? y / max : 0;
        progressBar.style.transform = "scaleX(" + progress + ")";
      }
      if (header) {
        header.classList.toggle("is-scrolled", y > 8);
      }
    }
    if (backToTop) {
      backToTop.addEventListener("click", function () {
        window.scrollTo({ top: 0, behavior: reducedMotion ? "auto" : "smooth" });
      });
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    onScroll();
  }

  /* ============================================================
   * Mega menu hover/focus open-close
   * ========================================================== */
  function initMegaMenu() {
    // Header mega menus are controlled by the state-based controller
    // in sections/header.liquid. This function intentionally does not
    // add a second hover controller that could fight that state.
    qsa("[data-mega-panel]").forEach(function (panel) {
      if (!panel.hasAttribute("aria-hidden")) {
        panel.setAttribute("aria-hidden", panel.hidden ? "true" : "false");
      }
    });
  }

  /* ============================================================
   * Predictive search
   * ========================================================== */
  function initPredictiveSearch() {
    var input = qs("[data-predictive-search]");
    if (!input) return;
    var results = qs("[data-predictive-results]");
    var handler = debounce(function () {
      var term = input.value.trim();
      if (!term) {
        if (results) results.innerHTML = "";
        return;
      }
      var url =
        (routes.predictive_search_url || "/search/suggest.json") +
        "?q=" + encodeURIComponent(term) +
        "&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last";
      fetch(url, { headers: { Accept: "application/json" } })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (!results) return;
          var products = (data.resources && data.resources.results && data.resources.results.products) || [];
          if (!products.length) {
            results.innerHTML = '<p class="text-[14px] text-muted-foreground sm:col-span-2 lg:col-span-4">' + (('general.search.no_results' in strings ? strings["general.search.no_results"] : "No results for \u201c" + term + "\u201d.")) + "</p>";
            return;
          }
          results.innerHTML = products
            .map(function (p) {
              return (
                '<a href="' + p.url + '" class="lift group flex items-center gap-4 rounded-2xl border border-hairline bg-card p-3 text-left">' +
                '<span class="media block size-16 shrink-0 rounded-xl">' +
                (p.featured_image ? '<img src="' + p.featured_image.url + '" alt="' + (p.featured_image.alt || p.title) + '" class="size-full object-cover img-fade img-shown" data-smart-image>' : "") +
                "</span>" +
                '<span class="min-w-0">' +
                '<span class="block truncate text-[14px] font-medium text-foreground">' + p.title + "</span>" +
                '<span class="nums mt-1 block text-[14px] font-semibold text-primary">' + p.price + "</span>" +
                "</span></a>"
              );
            })
            .join("");
          initSmartImages(results);
        })
        .catch(function () {
          if (results) results.innerHTML = "";
        });
    }, 220);
    input.addEventListener("input", handler);
  }

  /* ============================================================
   * Boot
   * ========================================================== */
  function init() {
    initReveal();
    initSmartImages();
    initCountUp();
    initTabs();
    initScrollers();
    initAutoplay();
    initOverlays();
    initQuickAdd();
    initCartLineActions();
    initQuickView();
    initVideoLightbox();
    initBeforeAfter();
    initAccordion();
    initChrome();
    initMegaMenu();
    initPredictiveSearch();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Re-scan for hooks whenever new markup streams in (quick view, drawer, etc.)
  if ("MutationObserver" in window) {
    var mo = new MutationObserver(
      debounce(function () {
        initReveal();
        initSmartImages();
        initCountUp();
        initScrollers();
        initAutoplay();
        initBeforeAfter();
        initAccordion();
        initMegaMenu();
      }, 80),
    );
    mo.observe(document.body, { childList: true, subtree: true });
  }

  window.Uberdrinks = Object.assign(T, {
    openOverlay: openOverlay,
    closeOverlay: closeOverlay,
    showToast: showToast,
    refreshCartDrawer: refreshCartDrawer,
  });
})();


/* =========================================================
   MEGA MENU — STATE BASED CONTROLLER
   This block is intentionally isolated from all other theme JS.
   It does not modify or remove unrelated theme functionality.
   ========================================================= */
(function () {
  function initTimeoMegaMenu() {
    const headers = document.querySelectorAll('[data-header][data-mega-hover]');
    if (!headers.length) return;

    headers.forEach(function (header) {
      if (header.dataset.timeoMegaInitialized === 'true') return;
      header.dataset.timeoMegaInitialized = 'true';

      const triggers = header.querySelectorAll('[data-mega-trigger]');
      const panels = header.querySelectorAll('[data-mega-panel]');
      if (!triggers.length || !panels.length) return;

      function closeAll() {
        panels.forEach(function (panel) {
          panel.hidden = true;
          panel.setAttribute('aria-hidden', 'true');
        });

        triggers.forEach(function (trigger) {
          trigger.setAttribute('aria-expanded', 'false');
        });
      }

      function open(key) {
        closeAll();

        const panel = header.querySelector(
          '[data-mega-panel="' + CSS.escape(key) + '"]'
        );

        const trigger = header.querySelector(
          '[data-mega-trigger="' + CSS.escape(key) + '"]'
        );

        if (!panel) return;

        panel.hidden = false;
        panel.setAttribute('aria-hidden', 'false');

        if (trigger) {
          trigger.setAttribute('aria-expanded', 'true');
        }
      }

      triggers.forEach(function (trigger) {
        const key = trigger.getAttribute('data-mega-trigger');
        if (!key) return;

        trigger.addEventListener('mouseenter', function () {
          open(key);
        });

        trigger.addEventListener('focus', function () {
          open(key);
        });

        trigger.addEventListener('click', function (event) {
          const panel = header.querySelector(
            '[data-mega-panel="' + CSS.escape(key) + '"]'
          );

          if (!panel) return;

          if (panel.hidden) {
            event.preventDefault();
            open(key);
          } else {
            closeAll();
          }
        });
      });

      header.addEventListener('mouseleave', closeAll);

      header.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          closeAll();
        }
      });

      document.addEventListener('click', function (event) {
        if (!header.contains(event.target)) {
          closeAll();
        }
      });

      function viewportCheck() {
        if (window.innerWidth < 1024) {
          closeAll();
        }
      }

      window.addEventListener('resize', viewportCheck);
      closeAll();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTimeoMegaMenu);
  } else {
    initTimeoMegaMenu();
  }

  document.addEventListener('shopify:section:load', initTimeoMegaMenu);
})();
